import { logger } from "../../utils/logger";
import { Star, MessageSquare, Sparkles } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { getApiUrl, getApiHeaders, getPublicUrl } from '../../utils/apiConfig';

interface Testimonial {
  id: string;
  quote: string;
  name: string;
  title: string;
  company: string;
  gradient?: string;
  logo?: string;
  verified?: boolean;
  rating: number;
  order: number;
}

interface TestimonialsData {
  badgeText: string;
  badgeIcon: string;
  sectionTitle: string;
  sectionSubtitle: string;
  visible: boolean;
  testimonials: Testimonial[];
}

export function TestimonialsSection() {
  const [data, setData] = useState<TestimonialsData | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Carousel state
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const dragThreshold = 50;
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchTestimonials();

    // Real-time updates when tab becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {

        fetchTestimonials();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024); // lg breakpoint
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const fetchTestimonials = async () => {
    try {

      const response = await fetch(getApiUrl('/content/testimonials'), {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {

        setData(getDefaultData());
        return;
      }

      const result = await response.json();

      // Handle both response formats:
      // 1. Wrapped: { success: true, data: {...} }
      // 2. Direct: { badgeText: '...', testimonials: [...] }
      if (result.success && result.data) {
        logger.log('✅ [TESTIMONIALS] Using CMS data (wrapped format)!');

        setData(result.data);
      } else if (result.badgeText && result.testimonials) {
        logger.log('✅ [TESTIMONIALS] Using CMS data (direct format)!');

        setData(result);
      } else {

        setData(getDefaultData());
      }
    } catch (error: any) {

      setData(getDefaultData());
    } finally {
      setLoading(false);
    }
  };

  const getDefaultData = (): TestimonialsData => {
    return {
      badgeText: 'Testimonials',
      badgeIcon: 'Star',
      sectionTitle: 'Trusted by Leading Manufacturers',
      sectionSubtitle: 'Our animations help global manufacturing companies communicate complex processes, accelerate decisions, and achieve measurable outcomes.',
      visible: true,
      testimonials: [
        {
          id: 't1',
          quote: "Creabiz transformed our technical documentation into stunning 3D animations. Our sales cycle reduced by 30% and customer understanding increased dramatically. The ROI was evident within the first quarter.",
          name: "Michael Chen",
          title: "VP of Operations",
          company: "Global Manufacturing Corp",
          gradient: "from-[#E87D3E] to-[#E8B84D]",
          verified: true,
          rating: 5,
          order: 1
        },
        {
          id: 't2',
          quote: "The quality and precision of their animations exceeded our expectations. Training new employees now takes half the time, and we've seen a significant reduction in assembly errors.",
          name: "Sarah Williams",
          title: "Director of Training",
          company: "Energy Solutions Inc",
          gradient: "from-[#E8B84D] to-[#E87D3E]",
          verified: true,
          rating: 5,
          order: 2
        }
      ]
    };
  };

  // Auto-slide effect
  useEffect(() => {
    if (isPaused || isDragging || !data?.testimonials) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => {
        const testimonials = data.testimonials || [];
        const maxSlide = isMobile ? testimonials.length - 1 : Math.max(0, testimonials.length - 2);
        return prev >= maxSlide ? 0 : prev + 1;
      });
    }, 4000);

    return () => clearInterval(interval);
  }, [isPaused, isDragging, data, isMobile]);

  // Drag handlers
  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    setIsDragging(true);
    setIsPaused(true);
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    setStartX(clientX);
  };

  const handleDragMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging) return;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const diff = clientX - startX;
    setDragOffset(diff);
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    
    const testimonials = data?.testimonials || [];
    const maxSlide = isMobile ? testimonials.length - 1 : Math.max(0, testimonials.length - 2);
    
    if (dragOffset < -dragThreshold) {
      // Dragged left - next slide
      setCurrentSlide((prev) => prev >= maxSlide ? 0 : prev + 1);
    } else if (dragOffset > dragThreshold) {
      // Dragged right - previous slide
      setCurrentSlide((prev) => prev <= 0 ? maxSlide : prev - 1);
    }
    
    setIsDragging(false);
    setDragOffset(0);
    setTimeout(() => setIsPaused(false), 100);
  };

  if (loading) {
    return (
      <section className="relative py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-white to-gray-100">
        <div className="max-w-7xl mx-auto text-center">
          <div className="animate-pulse text-gray-400">Loading testimonials...</div>
        </div>
      </section>
    );
  }

  if (!data || !data.visible) {
    return null;
  }

  return (
    <section className="relative py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden">
      {/* Premium Matte Background with Gradient Overlays */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-96 h-96 bg-gradient-to-br from-[#E8B84D] via-[#E87D3E] to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-tr from-[#2F2E5C] via-[#4A4878] to-transparent rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-12 sm:mb-16">
          {/* Premium Frosted Glass Badge */}
          <div className="inline-flex items-center gap-2 bg-white/60 backdrop-blur-xl px-4 py-2 rounded-full mb-4 border border-white/40 shadow-lg">
            {data.badgeIcon === 'Star' && <Star className="w-4 h-4 text-[#E8B84D] fill-[#E8B84D]" />}
            {data.badgeIcon === 'MessageSquare' && <MessageSquare className="w-4 h-4 text-[#E8B84D]" />}
            {data.badgeIcon === 'Sparkles' && <Sparkles className="w-4 h-4 text-[#E8B84D]" />}
            <span className="text-sm font-semibold bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] bg-clip-text text-transparent">
              {data.badgeText}
            </span>
          </div>
          
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 text-[#2F2E5C]">
            {data.sectionTitle}
          </h2>
          <p className="text-base text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {data.sectionSubtitle}
          </p>
        </div>
        
        {/* Auto-Sliding Carousel */}
        <div 
          ref={containerRef}
          className="max-w-6xl mx-auto overflow-hidden relative px-4 py-4 cursor-grab active:cursor-grabbing"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => {
            setIsPaused(false);
            if (isDragging) handleDragEnd();
          }}
          onMouseDown={handleDragStart}
          onMouseMove={handleDragMove}
          onMouseUp={handleDragEnd}
          onTouchStart={handleDragStart}
          onTouchMove={handleDragMove}
          onTouchEnd={handleDragEnd}
        >
          {(() => {
            const allTestimonials = Array.isArray(data.testimonials) ? data.testimonials : [];

            // Calculate transform based on mobile vs desktop
            const baseTransform = isMobile 
              ? `calc(-${currentSlide * 100}% - ${currentSlide * 16}px)`
              : `calc(-${currentSlide * 50}% - ${currentSlide * 16}px)`;
            
            const transformValue = `translateX(calc(${baseTransform} + ${dragOffset}px))`;

            return (
              <>
                {/* Flex Container for All Cards */}
                <div
                  className={`flex gap-4 lg:gap-16 ${isDragging ? '' : 'transition-transform duration-700 ease-in-out'}`}
                  style={{
                    transform: transformValue,
                    userSelect: isDragging ? 'none' : 'auto',
                    pointerEvents: isDragging ? 'none' : 'auto'
                  }}
                >
                  {allTestimonials.map((testimonial: any, i: number) => (
                    <div key={testimonial.id || i} className="group relative bg-white/80 backdrop-blur-sm rounded-3xl p-6 lg:p-8 shadow-lg border border-white/60 hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 overflow-hidden w-full lg:w-[calc(50%-32px)] flex-shrink-0">
                      {/* Premium gradient accent on hover */}
                      <div className={`absolute inset-0 bg-gradient-to-br ${testimonial.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />
                      
                      {/* Decorative corner gradient */}
                      <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl ${testimonial.gradient} opacity-5 rounded-bl-full`} />
                      
                      <div className="relative z-10">
                        {/* Star Rating with Premium Gradient */}
                        <div className="flex gap-1 mb-6">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <div key={star} className="relative">
                              <Star className={`w-5 h-5 fill-[#E8B84D] text-[#E8B84D] transition-all duration-300 group-hover:scale-110`} style={{transitionDelay: `${star * 50}ms`}} />
                            </div>
                          ))}
                        </div>
                        
                        {/* Quote with Premium Typography */}
                        <div className="mb-6 relative">
                          {/* Large quote mark */}
                          <div className={`absolute -top-2 -left-2 text-6xl font-serif bg-gradient-to-r ${testimonial.gradient} bg-clip-text text-transparent opacity-20`}>"</div>
                          <p className="text-base text-gray-700 leading-relaxed relative z-10 pl-4">
                            {testimonial.quote}
                          </p>
                        </div>
                        
                        {/* Author Info - Premium Layout */}
                        <div className="flex items-center gap-4 pt-6 border-t border-gray-200">
                          {/* Premium Avatar with Gradient Border */}
                          <div className="relative">
                            <div className={`absolute inset-0 bg-gradient-to-br ${testimonial.gradient} rounded-full blur-md opacity-50`} />
                            {testimonial.logo && !testimonial.logo.includes('creabiz.co/wp-content') ? (
                              <div className="relative w-14 h-14 rounded-full overflow-hidden bg-white border-2 border-white shadow-lg">
                                <img 
                                  src={testimonial.logo} 
                                  alt={testimonial.company}
                                  className="w-full h-full object-contain p-1"
                                  onError={(e) => {
                                    // Hide image and show fallback initials on error
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = 'none';
                                    const parent = target.parentElement;
                                    if (parent) {
                                      parent.className = `relative w-14 h-14 bg-gradient-to-br ${testimonial.gradient} rounded-full flex items-center justify-center shadow-lg border-2 border-white`;
                                      parent.innerHTML = `<span class="text-white font-bold text-lg">${testimonial.name.split(' ').map((n: string) => n[0]).join('')}</span>`;
                                    }
                                  }}
                                />
                              </div>
                            ) : (
                              <div className={`relative w-14 h-14 bg-gradient-to-br ${testimonial.gradient} rounded-full flex items-center justify-center shadow-lg border-2 border-white`}>
                                <span className="text-white font-bold text-lg">
                                  {testimonial.name.split(' ').map((n: string) => n[0]).join('')}
                                </span>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex-1">
                            <div className="font-bold text-[#2F2E5C] mb-0.5">{testimonial.name}</div>
                            <div className="text-sm text-gray-600">{testimonial.title}</div>
                            <div className="text-xs text-gray-500 mt-0.5">{testimonial.company}</div>
                          </div>
                          
                          {/* Premium Badge */}
                          {testimonial.verified && (
                            <div className={`hidden sm:block px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r ${testimonial.gradient} text-white shadow-md`}>
                              Verified
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
        </div>

        {/* Elegant Dot Navigation */}
        <div className="flex justify-center items-center gap-2 mt-12">
          {(() => {
            const testimonials = data.testimonials || [];
            const dotCount = isMobile ? testimonials.length : Math.max(0, testimonials.length - 1);
            return Array.from({ length: dotCount }).map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`transition-all duration-300 ${
                  currentSlide === index
                    ? 'w-8 h-2.5 bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] rounded-full'
                    : 'w-2.5 h-2.5 bg-gray-300 hover:bg-gray-400 rounded-full'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ));
          })()}
        </div>
      </div>
    </section>
  );
}